import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, TrendChart } from "@/components/charts/TrendChart";
import {
  Badge,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  Skeleton,
  StatCard,
} from "@/components/ui";
import type { components } from "@/generated/openapi-types";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { count, decimal, percent } from "@/lib/format";

type Metric = components["schemas"]["AdminStatsMetric"];

const METRICS: { id: Metric; label: string; noun: string }[] = [
  { id: "users", label: "Signups", noun: "signups" },
  { id: "reviews", label: "Reviews", noun: "reviews" },
  { id: "checkIns", label: "Check-ins", noun: "check-ins" },
  { id: "photos", label: "Photos", noun: "photos" },
  { id: "cafes", label: "Cafes", noun: "cafes" },
  { id: "proposals", label: "Proposals", noun: "proposals" },
  { id: "feedback", label: "Feedback", noun: "messages" },
];

/**
 * The screen the console opens on.
 *
 * Structure is deliberate: five totals across the top, then the one chart
 * that can be about any of them, then the two things that are specific to
 * this product — how much of the corpus we can actually say something about,
 * and how many people are doing anything.
 *
 * Every number here counts REAL people. `users.is_internal` marks the
 * team's own accounts and the dashboard queries exclude them, because on a
 * product this early four of our own logins is the difference between a
 * flat week and a good one.
 */
export function DashboardScreen() {
  const api = useApi();
  const [metric, setMetric] = useState<Metric>("reviews");

  const dashboard = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/dashboard");
      if (error) throw error;
      return data;
    },
  });

  const stats = useQuery({
    queryKey: ["admin", "stats", metric],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/dashboard/stats/{metric}", {
        params: { path: { metric } },
      });
      if (error) throw error;
      return data;
    },
  });

  const d = dashboard.data;
  const active = METRICS.find((m) => m.id === metric) ?? METRICS[0];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="Every figure counts real accounts only — team logins are flagged internal and excluded upstream."
      />

      {dashboard.isError ? (
        <ErrorState onRetry={() => void dashboard.refetch()} />
      ) : !d ? (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton
              key={i}
              className={cn("h-[126px]", i === 4 && "col-span-2 xl:col-span-1")}
            />
          ))}
        </div>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 xl:grid-cols-5">
            <StatCard
              label="People"
              value={d.users.total}
              delta={d.users.last7Days}
              deltaLabel="this week"
              tone="green"
            />
            <StatCard
              label="Reviews"
              value={d.reviews.total}
              delta={d.reviews.last7Days}
              deltaLabel="this week"
              tone="pink"
            />
            <StatCard
              label="Check-ins"
              value={d.checkIns.total}
              delta={d.checkIns.last7Days}
              deltaLabel="this week"
            />
            <StatCard
              label="Photos"
              value={d.photos.total}
              delta={d.photos.last7Days}
              deltaLabel="this week"
            />
            <StatCard
              label="Cafes in the corpus"
              value={d.cafes.total}
              className="col-span-2 xl:col-span-1"
              footer={`${count(d.cafes.citiesCovered)} cities · ${count(
                d.cafes.open,
              )} open`}
            />
          </section>

          <Card className="overflow-hidden">
            <CardHeader
              title="Over time"
              subtitle="Daily counts, last 30 days. Hover for a single day."
              action={
                <div className="flex min-w-max gap-1">
                  {METRICS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMetric(m.id)}
                      className={cn(
                        "rounded-chip border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                        m.id === metric
                          ? "border-brand-green bg-brand-green text-on-brand"
                          : "border-transparent text-muted hover:bg-surface-2 hover:text-ink-2",
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              }
            />
            {stats.isError ? (
              <ErrorState onRetry={() => void stats.refetch()} />
            ) : !stats.data ? (
              <Skeleton className="h-[168px]" />
            ) : (
              <>
                <TrendChart
                  points={stats.data.daily.map((p) => ({
                    at: p.day,
                    value: p.count,
                  }))}
                  label={active?.noun ?? ""}
                />
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border-2 pt-3 sm:grid-cols-6">
                  {(
                    [
                      ["Last hour", stats.data.buckets.lastHour],
                      ["24 hours", stats.data.buckets.last24Hours],
                      ["48 hours", stats.data.buckets.last48Hours],
                      ["7 days", stats.data.buckets.last7Days],
                      ["30 days", stats.data.buckets.last30Days],
                      ["All time", stats.data.buckets.allTime],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label}>
                      <p className="eyebrow">{label}</p>
                      <p className="font-mono text-[16px] text-ink mt-0.5">
                        {count(value)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 border-t border-border-2 pt-4">
                  <BarChart
                    points={stats.data.weekly.map((p) => ({
                      at: p.weekStart,
                      value: p.count,
                    }))}
                    label={active?.noun ?? ""}
                  />
                </div>
              </>
            )}
          </Card>

          <section className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="What we can say about the corpus"
                subtitle="A cafe is only useful once somebody has been and confirmed it serves matcha."
              />
              <CoverageBar
                total={d.cafes.total}
                confirmed={d.cafes.withConfirmedVerdict}
                likely={d.cafes.withLikelyVerdict}
              />
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
                <Figure
                  label="Confirmed"
                  value={d.cafes.withConfirmedVerdict}
                  hint="a person said so"
                />
                <Figure
                  label="Likely"
                  value={d.cafes.withLikelyVerdict}
                  hint="We think so · nobody has confirmed"
                />
                <Figure
                  label="Rated"
                  value={d.cafes.withScore}
                  hint="has a score"
                />
                <Figure
                  label="Cities live"
                  value={d.cafes.citiesLaunched}
                  hint={`of ${count(d.cafes.citiesCovered)} covered`}
                />
              </dl>
            </Card>

            <Card>
              <CardHeader
                title="People, and what they do"
                subtitle="Active means posted something — a review, a check-in or a photo. The API never sees a session."
              />
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <Figure
                  label="Active, 7 days"
                  value={d.engagement.activeUsersLast7Days}
                  hint={`${percent(
                    d.users.total > 0
                      ? (d.engagement.activeUsersLast7Days / d.users.total) *
                          100
                      : 0,
                  )} of everyone`}
                />
                <Figure
                  label="Active, 30 days"
                  value={d.engagement.activeUsersLast30Days}
                />
                <Figure
                  label="Have reviewed"
                  value={d.engagement.usersWithReview}
                  hint={`${percent(
                    d.users.total > 0
                      ? (d.engagement.usersWithReview / d.users.total) * 100
                      : 0,
                  )} of everyone`}
                />
                <Figure
                  label="Claimed a handle"
                  value={d.engagement.usersWithHandle}
                />
                <Figure
                  label="Reviews with a photo"
                  value={d.engagement.reviewsWithPhoto}
                  hint={`of ${count(d.reviews.total)}`}
                />
                <Figure
                  label="Average rating"
                  value={
                    d.engagement.averageMatchaRating === null
                      ? "—"
                      : decimal(d.engagement.averageMatchaRating)
                  }
                  hint="out of 5"
                />
              </dl>
            </Card>
          </section>

          <Card>
            <CardHeader
              title="Waiting on somebody"
              subtitle="Every one of these is work a person has to do. Zero is the goal."
            />
            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <Queue
                to="/reports"
                label="Open reports"
                value={d.moderation.openReports}
              />
              <Queue
                to="/proposals"
                label="Cafe proposals"
                value={d.moderation.pendingProposals}
              />
              <Queue
                to="/corrections"
                label="Fact corrections"
                value={d.moderation.pendingCorrections}
              />
              <Queue
                to="/feedback"
                label="New feedback"
                value={d.moderation.newFeedback}
              />
              <Queue
                to="/ops"
                label="Failed photos"
                value={d.moderation.failedPhotos}
              />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="font-mono text-[18px] text-ink mt-0.5 leading-none">
        {typeof value === "number" ? count(value) : value}
      </dd>
      {hint ? <dd className="text-micro text-muted mt-1">{hint}</dd> : null}
    </div>
  );
}

/**
 * A single stacked bar rather than a donut.
 *
 * The question it answers is "how much of the corpus is settled", and that
 * is a part-of-whole comparison where the whole is enormous and the parts
 * are small — which a donut renders as three slivers and a bar renders
 * honestly. The unknown remainder deliberately takes most of the width.
 */
function CoverageBar({
  total,
  confirmed,
  likely,
}: {
  total: number;
  confirmed: number;
  likely: number;
}) {
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-chip bg-surface-3">
        <div
          className="bg-brand-green"
          style={{ width: `${pct(confirmed)}%` }}
          title={`${confirmed} confirmed`}
        />
        <div
          className="bg-brand-pink"
          style={{ width: `${pct(likely)}%` }}
          title={`${likely} · We think so; nobody has confirmed`}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-micro text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-green" />
          confirmed by a person
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-pink" />
          We think so · nobody has confirmed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-surface-3" />
          nothing said yet
        </span>
      </div>
    </div>
  );
}

function Queue({
  to,
  label,
  value,
}: {
  to: string;
  label: string;
  value: number;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center justify-between gap-2 rounded-control border px-3 py-2.5 transition-colors",
        value > 0
          ? "border-accent/30 bg-accent-soft hover:border-accent/60"
          : "border-border bg-surface-2/50 hover:border-muted-2",
      )}
    >
      <span className="text-[12px] text-ink-2">{label}</span>
      {value > 0 ? (
        <span className="font-mono text-[15px] font-semibold text-accent">
          {count(value)}
        </span>
      ) : (
        <Badge tone="neutral">clear</Badge>
      )}
    </Link>
  );
}
